import LinkButton from '@carvana/ds-link-button';
import Skeleton from '@carvana/ds-skeleton';
import { icon } from '@carvana/ds-styles';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import cx from 'classnames';
import { Heart, Share } from 'lucide-react';
import { useCallback, useContext } from 'react';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import { useSectionAnalytics } from '~/context/vdp/SectionAnalyticsContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleIdAndStockNumber } from '~/context/vdp/VehicleContext';
import { useToggleFavorite } from '~/hooks/vdp/favorites';
import useShareLink from '~/hooks/vdp/useShareLink';

function useIsFavoritesLoading() {
  const isLoggedIn = useIsLoggedIn();
  const {
    favorites: { hasEverLoaded }
  } = useContext(UserContext);

  return isLoggedIn && !hasEverLoaded;
}

const linkButtonStyles = 'flex flex-1 items-center justify-center gap-md';

type OwnProps = {
  showTopBorder?: boolean;
};

export default function DesktopActions({ showTopBorder = false }: OwnProps) {
  const { isBreadcrumbScrolledOut } = useContext(GlobalContext);
  const { isFavorite } = useContext(UserVehicleContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { analyticsAttributes, analyticsId } = useSectionAnalytics();
  const isLoading = useIsFavoritesLoading();
  const shareLink = useShareLink();
  const { stockNumber, vehicleId } = useGetVehicleIdAndStockNumber();
  const toggleFavorite = useToggleFavorite();

  const handleFavoriteClick = useCallback(() => {
    if (analyticsId) {
      trackEvent(analyticsId, {
        ...analyticsAttributes,
        action: 'clicked',
        description: isFavorite ? 'favorite remove' : 'favorite add',
        vehicleId
      });
    }
    toggleFavorite({ stockNumber, vehicleId });
  }, [
    analyticsAttributes,
    analyticsId,
    isFavorite,
    stockNumber,
    toggleFavorite,
    trackEvent,
    vehicleId
  ]);

  return (
    <div
      aria-hidden={!isBreadcrumbScrolledOut || undefined}
      className={cx(
        'overflow-hidden transition-all duration-300 ease-in-out',
        isBreadcrumbScrolledOut
          ? 'max-h-[100px] opacity-100'
          : 'max-h-0 opacity-0'
      )}
    >
      <div
        className={cx(
          'flex gap-4xl rounded-b-xl bg-background-weak px-4xl py-xl',
          { 'border-t border-border-subtle': showTopBorder }
        )}
      >
        <LinkButton
          data-adobe-id="share"
          data-analytics-id={analyticsId}
          data-analytics-attributes={JSON.stringify({
            ...analyticsAttributes,
            description: 'share'
          })}
          data-testid="vehicle-info-card-share"
          onClick={shareLink}
          className={linkButtonStyles}
          variant="default"
          size="sm"
          underline={false}
        >
          <Share size={16} /> Share
        </LinkButton>
        <span className="text-border-weak">|</span>

        {isLoading ? (
          <div className="flex h-2xl flex-1 items-center justify-center">
            <Skeleton width="124px" height="24px" className="m-0" />
          </div>
        ) : (
          <LinkButton
            data-adobe-id={null}
            className={linkButtonStyles}
            variant="default"
            size="sm"
            data-analytics-id={null}
            data-testid="vehicle-info-card-favorite"
            onClick={handleFavoriteClick}
            underline={false}
          >
            <Heart
              size={16}
              color={isFavorite ? icon.critical : 'currentColor'}
              className={isFavorite ? 'fill-icon-critical' : ''}
            />
            {isFavorite ? 'Saved' : 'Save'}
          </LinkButton>
        )}
      </div>
    </div>
  );
}
